import * as XLSX from 'xlsx';

/**
 * 현재 경로를 기반으로 파일명과 시트명 자동 생성 (fallback용)
 * @param pathname - 현재 경로 (예: '/pointhome/adpopcorn')
 * @returns { filename: string, sheetName: string }
 */
export function getExcelFileNameFromPath(pathname: string): { filename: string; sheetName: string } {
  // 경로를 기반으로 자동 생성
  const parts = pathname.split('/').filter(Boolean);
  const filename = parts.join('_') || 'export';
  const sheetName = parts[parts.length - 1] || 'Sheet1';

  return {
    filename: `${filename}.xlsx`,
    sheetName: sheetName
  };
}

/**
 * 테이블 데이터를 엑셀 파일로 다운로드
 * @param data - 다운로드할 데이터 배열 (객체 배열 또는 2D 배열)
 * @param filename - 파일명 (예: '포인트홈_애드팝콘.xlsx')
 * @param sheetName - 시트명 (기본값: 'Sheet1')
 * @param headers - 헤더 배열 (선택사항, data가 객체 배열이면 자동으로 키 사용)
 * @param columnWidths - 컬럼 너비 배열 (선택사항)
 */
export function exportToExcel(
  data: Record<string, unknown>[] | (string | number)[][],
  filename: string,
  sheetName = 'Sheet1',
  headers?: string[],
  columnWidths?: number[]
) {
  try {
    const wb = XLSX.utils.book_new();
    let ws: XLSX.WorkSheet;

    // 데이터가 2D 배열인 경우 (헤더 + 데이터)
    if (Array.isArray(data[0]) && typeof data[0][0] !== 'object') {
      ws = XLSX.utils.aoa_to_sheet(data as (string | number)[][]);
    } 
    // 데이터가 객체 배열인 경우
    else {
      ws = XLSX.utils.json_to_sheet(data as Record<string, unknown>[]);
    }

    // 헤더가 별도로 제공된 경우 첫 번째 행에 추가
    if (headers && headers.length > 0) {
      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A1' });
    }

    // 컬럼 너비 설정
    if (columnWidths && columnWidths.length > 0) {
      ws['!cols'] = columnWidths.map(width => ({ wch: width }));
    }

    // 워크시트를 워크북에 추가
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // 파일 다운로드
    XLSX.writeFile(wb, filename);
  } catch (error) {
    throw error;
  }
}

/**
 * 테이블 데이터를 CSV 파일로 다운로드
 * @param data - 다운로드할 데이터 배열
 * @param filename - 파일명 (예: '포인트홈_애드팝콘.csv')
 * @param headers - 헤더 배열 (선택사항)
 */
export function exportToCsv(
  data: Record<string, unknown>[] | (string | number)[][],
  filename: string,
  headers?: string[]
) {
  try {
    let csvContent = '';

    // 헤더 추가
    if (headers && headers.length > 0) {
      csvContent += headers.join(',') + '\n';
    } else if (data.length > 0 && typeof data[0] === 'object' && !Array.isArray(data[0])) {
      // 객체 배열인 경우 키를 헤더로 사용
      csvContent += Object.keys(data[0]).join(',') + '\n';
    }

    // 데이터 추가
    data.forEach((row) => {
      if (Array.isArray(row)) {
        csvContent += row.join(',') + '\n';
      } else {
        csvContent += Object.values(row).join(',') + '\n';
      }
    });

    // BOM 추가 (한글 깨짐 방지)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    throw error;
  }
}

/**
 * HTML 테이블을 엑셀 파일로 다운로드
 * @param table - HTML 테이블 엘리먼트
 * @param fileName - 파일명 (기본값: 'table.xlsx')
 * @param sheetName - 시트명 (기본값: 'Sheet1')
 * @param allRowsData - 페이지네이션 무시하고 모든 행을 포함하기 위한 전체 데이터 (선택사항)
 *                       제공되면 이 데이터로 임시 테이블을 생성하여 엑셀로 변환
 */
export function exportTableToExcel(
  table: HTMLTableElement,
  fileName = 'table.xlsx',
  sheetName = 'Sheet1',
  allRowsData?: { headers: string[], rows: string[][] }
) {
  try {
    let worksheet: XLSX.WorkSheet;

    // 전체 데이터가 제공된 경우 AOA로 바로 변환
    if (allRowsData && allRowsData.rows.length > 0) {
      const aoa = [allRowsData.headers, ...allRowsData.rows];
      worksheet = XLSX.utils.aoa_to_sheet(aoa);
    } else {
      // HTML <table> → Sheet
      worksheet = XLSX.utils.table_to_sheet(table);
      normalizeCurrencyCells(worksheet);
    }

    // 2) 워크북 생성 & 시트 추가
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // 3) 파일 다운로드
    XLSX.writeFile(workbook, fileName);
  } catch (error) {
    throw error;
  }
}

/**
 * 통화/천단위 콤마 텍스트 셀을 실제 숫자로 변환 (SUM 등 엑셀 수식 사용 가능하도록)
 * 날짜(대시 포함)나 퍼센트(%), 증감 표시(▲▼)는 건드리지 않도록 전체 문자열이
 * 통화 기호·콤마 제거 후 순수 숫자로만 이루어진 경우에만 변환한다.
 */
function normalizeCurrencyCells(worksheet: XLSX.WorkSheet) {
  const ref = worksheet['!ref'];
  if (!ref) return;
  const range = XLSX.utils.decode_range(ref);
  for (let row = range.s.r; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
      if (!cell || cell.t !== 's' || typeof cell.v !== 'string') continue;
      if (cell.v.includes('%') || cell.v.includes('▲') || cell.v.includes('▼')) continue;

      const cleaned = cell.v.replace(/[₩$원달러,\s]/g, '');
      if (!/^-?\d+(\.\d+)?$/.test(cleaned)) continue;

      cell.v = parseFloat(cleaned);
      cell.t = 'n';
      delete cell.w;
    }
  }
}

/**
 * 퍼센트 컬럼인지 확인하는 헬퍼 함수
 */
function isPercentColumn(header: string): boolean {
  const percentKeywords = [
    'rate', 'Rate', 'RATE',
    'CTR', 'ctr',
    'VTR', 'vtr',
    '수익률', '손익률', '전환율', '이탈률', '이탈율', '재방문률', '재방문율', '비율', '진행률', '수령률',
    '퍼센트', 'percent', 'Percent',
    'fill', 'Fill', 'FILL',
    'bounce', 'Bounce',
    'revisit', 'Revisit',
    'conversion', 'Conversion',
    'progress', 'Progress',
    'reward', 'Reward',
    'funnel', 'Funnel',
    '증감률', '증감',
    '유저비율'
  ];
  const headerLower = header.toLowerCase();
  return percentKeywords.some(keyword => headerLower.includes(keyword.toLowerCase())) || header.includes('%');
}

/**
 * 통화 기호 제거 및 숫자 변환
 */
function removeCurrencyAndParseNumber(value: string): number | string {
  // 증감 표시(▲▼)가 있으면 그대로 유지
  if (value.includes('▲') || value.includes('▼')) {
    return value;
  }

  // 통화 기호 제거 (₩, $, 원, 달러 등)
  const cleaned = value.replace(/[₩$원달러,]/g, '').trim();
  
  // 숫자로 변환 시도
  const numValue = parseFloat(cleaned);
  if (!isNaN(numValue)) {
    return numValue;
  }
  
  // 변환 실패하면 원본 반환
  return value;
}

/**
 * 퍼센트 값에서 숫자 추출 (예: "32.48%" → 0.3248, 또는 이미 숫자인 경우 그대로)
 */
function parsePercentValue(value: string | number): number | string {
  // 이미 숫자인 경우 (0.3248 같은 형태) 그대로 반환
  if (typeof value === 'number') {
    return value;
  }

  const valueStr = String(value);
  
  // 증감 표시(▲▼)가 있으면 그대로 유지
  if (valueStr.includes('▲') || valueStr.includes('▼')) {
    return valueStr;
  }

  // % 제거하고 숫자 추출
  const cleaned = valueStr.replace(/%/g, '').trim();
  const numValue = parseFloat(cleaned);
  
  if (!isNaN(numValue)) {
    // 퍼센트를 소수로 변환 (32.48 → 0.3248)
    // 이미 소수 형태(0.3248)인 경우는 그대로 반환
    return numValue > 1 ? numValue / 100 : numValue;
  }
  
  // 변환 실패하면 원본 반환
  return valueStr;
}

/**
 * 테이블에서 헤더를 추출하고 전체 데이터로 임시 테이블을 생성하여 엑셀로 변환
 * @param table - HTML 테이블 엘리먼트 (헤더 추출용)
 * @param allRowsData - 전체 행 데이터 (현재 정렬 상태 그대로)
 * @param fileName - 파일명
 * @param sheetName - 시트명
 */
export function exportTableToExcelWithAllRows(
  table: HTMLTableElement,
  allRowsData: (string | number)[][],
  fileName = 'table.xlsx',
  sheetName = 'Sheet1'
) {
  try {
    // 테이블에서 헤더 추출
    // 빈 셀(확장 버튼용)은 제외하고 추출
    const headers: string[] = [];
    const thead = table.querySelector('thead');
    if (thead) {
      const headerRow = thead.querySelector('tr');
      if (headerRow) {
        const headerCells = headerRow.querySelectorAll('th, td');
        headerCells.forEach((cell, index) => {
          // TableSortLabel 같은 내부 요소의 텍스트 추출
          const text = cell.textContent?.trim() || '';
          // 첫 번째 셀이 빈 경우(확장 버튼용) 제외
          if (index === 0 && !text) {
            return; // 빈 셀 건너뛰기
          }
          headers.push(text);
        });
      }
    }

    // 헤더가 없으면 allRowsData의 첫 번째 행을 헤더로 사용
    if (headers.length === 0 && allRowsData.length > 0) {
      headers.push(...allRowsData[0].map(cell => String(cell)));
      allRowsData = allRowsData.slice(1);
    }

    // 퍼센트 컬럼 인덱스 찾기 (합계 행 감지 전에 미리 계산)
    const percentColumnIndices = new Set<number>();
    headers.forEach((header, index) => {
      if (isPercentColumn(header)) {
        percentColumnIndices.add(index);
      }
    });

    // 테이블에서 합계 행 자동 감지
    const sumRowData: (string | number)[] | null = (() => {
      const tbody = table.querySelector('tbody');
      if (!tbody) return null;

      const rows = Array.from(tbody.querySelectorAll('tr'));
      for (const row of rows) {
        const firstCell = row.querySelector('td, th');
        const firstCellText = firstCell?.textContent?.trim() || '';
        
        // 첫 번째 셀이 "합계"인 경우
        if (firstCellText === '합계' || firstCellText.includes('합계')) {
          const cells = Array.from(row.querySelectorAll('td, th'));
          const rowData: (string | number)[] = [];
          let headerIndex = 0; // 헤더 인덱스 (빈 셀 제외 처리)
          
          cells.forEach((cell: Element, cellIndex: number) => {
            // 첫 번째 셀이 빈 경우(확장 버튼용) 제외
            if (cellIndex === 0 && !cell.textContent?.trim()) {
              return;
            }
            
            const text = cell.textContent?.trim() || '';
            // 툴팁 아이콘 제거 (InfoOutlinedIcon 등)
            const cleanText = text.replace(/\s*조회한 기간의 평균입니다\s*/g, '').trim();
            
            // 퍼센트 컬럼인지 확인 (헤더 기반)
            const isPercentCol = percentColumnIndices.has(headerIndex);
            
            if (isPercentCol && cleanText.includes('%')) {
              // 퍼센트 값 처리: "24.94%" → 0.2494
              const percentValue = parsePercentValue(cleanText);
              rowData.push(percentValue);
            } else {
              // 숫자로 변환 가능한지 확인
              const numValue = parseFloat(cleanText.replace(/[₩$원달러,]/g, ''));
              if (!isNaN(numValue) && cleanText !== '합계' && !cleanText.includes('합계')) {
                rowData.push(numValue);
              } else {
                rowData.push(cleanText);
              }
            }
            
            headerIndex++; // 헤더 인덱스 증가
          });
          
          // 헤더 개수와 맞추기 (빈 셀 제외 처리)
          if (rowData.length === headers.length) {
            return rowData;
          }
        }
      }
      return null;
    })();

    // 합계 행이 감지되었고 allRowsData에 포함되지 않은 경우 추가
    if (sumRowData && allRowsData.length > 0) {
      const firstRowFirstCell = String(allRowsData[0][0]);
      // allRowsData의 첫 번째 행이 합계가 아닌 경우에만 추가
      if (firstRowFirstCell !== '합계' && !firstRowFirstCell.includes('합계')) {
        allRowsData = [sumRowData, ...allRowsData];
      }
    }

    // 퍼센트 컬럼 인덱스는 이미 위에서 계산됨

    // 날짜 컬럼 인덱스 찾기 (전처리 단계에서 문자열로 유지하기 위해)
    const dateColumnIndices = new Set<number>();
    headers.forEach((header, index) => {
      const hLower = header.toLowerCase();
      if (hLower.includes('date') || header.includes('날짜') || header.includes('구분')) {
        dateColumnIndices.add(index);
      }
    });
    
    // 데이터 기반 날짜 컬럼 감지: YYYY-MM-DD 형식인 경우 자동 감지
    if (allRowsData.length > 0) {
      allRowsData[0].forEach((cell, colIndex) => {
        if (!dateColumnIndices.has(colIndex)) {
          const cellStr = String(cell);
          // YYYY-MM-DD 형식 감지 (예: 2025-01-15)
          const datePattern = /^\d{4}-\d{2}-\d{2}$/;
          if (datePattern.test(cellStr)) {
            dateColumnIndices.add(colIndex);
          }
        }
      });
    }

    // 데이터 전처리: 통화 기호 제거, 퍼센트는 숫자로 변환 준비, 날짜는 문자열 유지
    const processedData: (string | number)[][] = allRowsData.map(row => {
      return row.map((cell, colIndex) => {
        // 날짜 컬럼인 경우: 문자열로 유지 (엑셀 자동 변환 방지)
        if (dateColumnIndices.has(colIndex)) {
          return String(cell);
        }
        
        // 퍼센트 컬럼인 경우
        if (percentColumnIndices.has(colIndex)) {
          return parsePercentValue(cell);
        }
        
        // 일반 컬럼: 통화 기호만 제거
        const cellStr = String(cell);
        return removeCurrencyAndParseNumber(cellStr);
      });
    });

    // AOA(헤더 + 바디)로 바로 워크시트 생성
    const aoa: (string | number)[][] = [headers, ...processedData];
    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    
    // 퍼센트 컬럼에 numFmt 적용 (날짜는 이미 문자열로 처리됨)
    // 엑셀에서 헤더는 A1 (row 0), 데이터는 A2부터 시작 (row 1부터)
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    
    // 데이터 행만 처리 (헤더 제외, row 1부터 시작)
    for (let row = 1; row <= range.e.r; row++) {
      for (let col = 0; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];
        
        if (!cell) continue;
        
        // 날짜 컬럼인 경우: 문자열로 강제 고정 (엑셀 자동 날짜 변환 방지)
        if (dateColumnIndices.has(col)) {
          cell.v = String(cell.v);
          cell.t = 's'; // 문자열 타입 명시
        }
        
        // 퍼센트 컬럼인 경우
        if (percentColumnIndices.has(col)) {
          // 증감 표시(▲▼)가 있으면 문자열로 유지
          if (typeof cell.v === 'string' && (cell.v.includes('▲') || cell.v.includes('▼'))) {
            continue;
          }
          
          // 손익률 컬럼인 경우 (증감 부호 +% 포함 문자열)
          if (typeof cell.v === 'string' && (cell.v.includes('+') || cell.v.includes('-')) && cell.v.includes('%')) {
            // 이미 +22% 형식이면 그대로 유지
            continue;
          }
          
          // 숫자인 경우 퍼센트 포맷 적용
          if (typeof cell.v === 'number') {
            // 손익률 컬럼은 정수로 표시 (0%)
            const header = headers[col] || '';
            if (header.includes('손익률')) {
              cell.z = '0%';
            } else if (header.includes('이탈율') || header.includes('유저비율')) {
              // 이탈율과 유저비율은 소수점 첫째 자리까지 표시 (0.0%)
              cell.z = '0.0%';
            } else {
              cell.z = '0.00%';
            }
          }
        }

        // 퍼센트/날짜가 아닌 숫자 컬럼은 천단위 콤마 포맷 적용
        // 정수: "#,##0", 소수: "#,##0.00"
        if (!percentColumnIndices.has(col) && !dateColumnIndices.has(col) && typeof cell.v === 'number') {
          const isInteger = Number.isInteger(cell.v);
          cell.z = isInteger ? '#,##0' : '#,##0.00';
        }
      }
    }
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, fileName);
  } catch (error) {
    throw error;
  }
}

